import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Checkbox,
  Divider,
  Group,
  Stack,
  // eslint-disable-next-line no-restricted-imports -- Native Mantine Table needed here; the custom Table is a data grid and not applicable
  Table,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core'
import { IconCrown, IconPlus, IconSearch, IconTrash } from '@tabler/icons-react'
import { useMemo, useRef, useState } from 'react'

import type { AdditionalOwner, GetUsersResponseEntry } from '@/services/api/api.types'

type OwnerEntry = {
  userId: string
  name: string
  email: string
  bypassReview: boolean
}

interface AdditionalOwnersModalProps {
  users: GetUsersResponseEntry[]
  currentOwners: AdditionalOwner[]
  primaryOwnerId: string
  primaryOwnerName: string
  primaryOwnerEmail: string
  primaryOwnerIsPm: boolean
  onSave: (owners: AdditionalOwner[]) => void
  onClose: () => void
}

export function AdditionalOwnersModal({
  users,
  currentOwners,
  primaryOwnerId,
  primaryOwnerName,
  primaryOwnerEmail,
  primaryOwnerIsPm,
  onSave,
  onClose,
}: AdditionalOwnersModalProps) {
  const [searchQuery, setSearchQuery] = useState('')

  const primaryOwnerFromApi = currentOwners.find(o => o.userId === primaryOwnerId)
  const [primaryOwnerBypassReview, setPrimaryOwnerBypassReview] = useState(() => {
    if (primaryOwnerFromApi && primaryOwnerFromApi.hotEdit !== primaryOwnerFromApi.directPublish) {
      console.warn(`Primary owner ${primaryOwnerId} has mismatched hotEdit/directPublish — will be synced on save`)
    }
    return primaryOwnerFromApi?.hotEdit ?? false
  })

  // Product decision: hotEdit and directPublish are exposed as a single "Bypass Review"
  // toggle. If they differ on the backend, we treat either=true as checked and saving
  // will sync both to the same value.
  const [selectedOwners, setSelectedOwners] = useState<OwnerEntry[]>(() =>
    currentOwners
      .filter(o => o.userId !== primaryOwnerId)
      .map(o => {
        if (o.hotEdit !== o.directPublish) {
          console.warn(`Owner ${o.userId} has mismatched hotEdit/directPublish — will be synced on save`)
        }
        const user = users.find(u => u.userId === o.userId)
        return {
          userId: o.userId,
          name: o.name || user?.name || '',
          email: o.email || user?.email || '',
          bypassReview: (o.hotEdit ?? false) || (o.directPublish ?? false),
        }
      }),
  )

  const initialOwners = useRef(selectedOwners)
  const initialPrimaryBypassReview = useRef(primaryOwnerBypassReview)

  const hasChanges = useMemo(() => {
    if (primaryOwnerBypassReview !== initialPrimaryBypassReview.current) return true
    const initial = initialOwners.current
    if (initial.length !== selectedOwners.length) return true
    return selectedOwners.some((o, i) => {
      const orig = initial[i]
      return o.userId !== orig.userId || o.bypassReview !== orig.bypassReview
    })
  }, [selectedOwners, primaryOwnerBypassReview])

  const selectedUserIds = useMemo(
    () => new Set([primaryOwnerId, ...selectedOwners.map(o => o.userId)]),
    [primaryOwnerId, selectedOwners],
  )

  const availableUsers = useMemo(() => {
    return users
      .filter(u => !selectedUserIds.has(u.userId))
      .filter(u => {
        if (!searchQuery) return true
        const q = searchQuery.toLowerCase()
        return (u.name?.toLowerCase().includes(q) ?? false) || u.email.toLowerCase().includes(q)
      })
  }, [users, selectedUserIds, searchQuery])

  const handleAddUser = (user: GetUsersResponseEntry) => {
    setSelectedOwners(prev => [
      ...prev,
      {
        userId: user.userId,
        name: user.name || '',
        email: user.email,
        bypassReview: user.isSponsorAdmin ?? false,
      },
    ])
  }

  const handleRemove = (userId: string) => {
    setSelectedOwners(prev => prev.filter(o => o.userId !== userId))
  }

  const handleToggle = (userId: string) => {
    setSelectedOwners(prev => prev.map(o => (o.userId === userId ? { ...o, bypassReview: !o.bypassReview } : o)))
  }

  const handleSave = () => {
    const primaryEntry: AdditionalOwner = {
      userId: primaryOwnerId,
      hotEdit: primaryOwnerBypassReview,
      directPublish: primaryOwnerBypassReview,
      email: primaryOwnerEmail || undefined,
      name: primaryOwnerName || undefined,
    }
    onSave([
      primaryEntry,
      ...selectedOwners.map(o => ({
        userId: o.userId,
        hotEdit: o.bypassReview,
        directPublish: o.bypassReview,
        email: o.email || undefined,
        name: o.name || undefined,
      })),
    ])
  }

  return (
    <Stack gap="md" style={{ flex: 1, minHeight: 0 }}>
      <Group justify="space-between" align="center">
        <Text size="lg" fw={700}>
          Manage Owners
        </Text>
        <Group gap="sm">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!hasChanges}>
            Save
          </Button>
        </Group>
      </Group>

      <TextInput
        placeholder="Filter users..."
        leftSection={<IconSearch size={14} />}
        value={searchQuery}
        onChange={e => setSearchQuery(e.currentTarget.value)}
        size="xs"
        w={240}
        radius="xl"
      />

      <Box>
        <Text size="md" fw={600} mb="xs">
          Owners
        </Text>
        <Box style={{ maxHeight: 240, overflowY: 'auto' }}>
          <Table highlightOnHover stickyHeader>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Name</Table.Th>
                <Table.Th>Email</Table.Th>
                <Table.Th style={{ textAlign: 'center' }} w={130}>
                  <Tooltip label="Allows publishing and editing without admin approval" withArrow multiline w={220}>
                    <Text size="sm" fw={700} style={{ cursor: 'help' }}>
                      Bypass Review
                    </Text>
                  </Tooltip>
                </Table.Th>
                <Table.Th w={120} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              <Table.Tr style={{ backgroundColor: 'var(--mantine-color-blue-light)' }}>
                <Table.Td>
                  <Text size="sm" fw={600}>
                    {primaryOwnerName}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" c="dimmed">
                    {primaryOwnerEmail}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Group justify="center">
                    <Tooltip label="Admins always have full permissions" disabled={!primaryOwnerIsPm} withArrow>
                      <Box>
                        <Checkbox
                          checked={primaryOwnerIsPm || primaryOwnerBypassReview}
                          disabled={primaryOwnerIsPm}
                          onChange={() => setPrimaryOwnerBypassReview(v => !v)}
                        />
                      </Box>
                    </Tooltip>
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Tooltip label="Original creator" withArrow>
                    <Badge variant="light" color="blue" size="sm" leftSection={<IconCrown size={12} />}>
                      Creator
                    </Badge>
                  </Tooltip>
                </Table.Td>
              </Table.Tr>
              {selectedOwners.map(owner => {
                const ownerIsPm = users.find(u => u.userId === owner.userId)?.isSponsorAdmin ?? false
                return (
                  <Table.Tr key={owner.userId}>
                    <Table.Td>
                      <Text size="sm" fw={500}>
                        {owner.name}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed">
                        {owner.email}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Group justify="center">
                        <Tooltip label="Admins always have full permissions" disabled={!ownerIsPm} withArrow>
                          <Box>
                            <Checkbox
                              checked={ownerIsPm || owner.bypassReview}
                              disabled={ownerIsPm}
                              onChange={() => handleToggle(owner.userId)}
                            />
                          </Box>
                        </Tooltip>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Tooltip label="Remove owner" withArrow>
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          size="lg"
                          onClick={() => handleRemove(owner.userId)}
                          aria-label="Remove owner">
                          <IconTrash size={20} aria-hidden="true" />
                        </ActionIcon>
                      </Tooltip>
                    </Table.Td>
                  </Table.Tr>
                )
              })}
            </Table.Tbody>
          </Table>
        </Box>
      </Box>

      <Divider />
      <Text size="md" fw={600}>
        Available Users
      </Text>
      <Box style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        <Table highlightOnHover stickyHeader>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Name</Table.Th>
              <Table.Th>Email</Table.Th>
              <Table.Th style={{ textAlign: 'center' }} w={80}>
                Add
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {availableUsers.map(user => (
              <Table.Tr key={user.userId}>
                <Table.Td>
                  <Text size="sm">{user.name || ''}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" c="dimmed">
                    {user.email}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Group justify="center">
                    <Tooltip label="Add as owner" withArrow>
                      <ActionIcon
                        variant="light"
                        color="blue"
                        radius="sm"
                        onClick={() => handleAddUser(user)}
                        aria-label="Add as owner">
                        <IconPlus size={16} aria-hidden="true" />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
            {availableUsers.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={3}>
                  <Text size="sm" c="dimmed" ta="center" py="sm">
                    {searchQuery ? 'No users match your search' : 'All users are already owners'}
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Box>
    </Stack>
  )
}
